import type { HelpSectionsMap } from './types';

export const enHelpSections: HelpSectionsMap = {
  start: {
    title: 'Getting started',
    intro: 'A quick guide to get started in WERSTACK. Follow the steps in order to get comfortable faster.',
    blocks: [
      {
        title: 'Step 1. Choose a workspace',
        text: 'A workspace is your project with its own files, settings, and team members.',
        items: [
          'In the left sidebar, open "My" or "Shared" and select a workspace.',
          'Files and folders switch automatically.',
          '"Create" at the top of the sidebar — a new personal workspace (if under the limit).',
          'Each workspace can have its own language, theme, and accent color.',
        ],
        where: 'Left sidebar → "My" / "Shared". When collapsed, hover a section icon for the list.',
      },
      {
        title: 'Step 2. Upload your first files',
        items: [
          'Click "Upload images" in the gallery toolbar.',
          'Ctrl+Shift+V — paste a screenshot from the clipboard into the current folder.',
          'Up to 1000 files at once (JPEG, PNG, WebP; up to 10 MB each).',
          'A progress bar appears at the bottom — you can dismiss or cancel it.',
        ],
        where: 'Home screen → "Upload images" button on the top right.',
      },
      {
        title: 'Step 3. Create folders',
        items: [
          'Click "Create folder", enter a name, and confirm.',
          'Open a folder with a double-click (single tap on touch devices).',
          'At the workspace root, the toolbar shows the workspace name on the left.',
          'Inside a subfolder — breadcrumbs with a home icon to return to the root.',
        ],
        where: 'Home screen → "Create folder" next to upload.',
      },
      {
        title: 'Sidebar',
        items: [
          'WERSTACK logo and workspace lists at the top.',
          '"Create" — new workspace.',
          '"Data" — tables: "All" opens the catalog; lists below for quick open.',
          '"Boards" — kanban boards over tables.',
          '"Workflows" — automation graphs for the current workspace.',
          '"Galleries" — catalog of public galleries.',
          'Edge button — collapse or expand the sidebar.',
        ],
        where: 'Left column of the main screen.',
      },
      {
        title: 'Top bar buttons',
        items: [
          'Bell — team invitations and comment notifications.',
          'Question mark — this help panel (separate from profile).',
          'Initials — sign out.',
        ],
        where: 'Top right corner of the screen.',
      },
      {
        title: 'Roles and permissions',
        text: 'Not every action is available to every user.',
        items: [
          'Owner and Admin — full access, manage members and galleries.',
          'Editor — view and edit files, no team management.',
          'Viewer — view only, no upload or delete.',
        ],
        tip: 'If a button is disabled or a menu item is missing, you may have the Viewer role.',
      },
    ],
  },
  gallery: {
    title: 'Gallery: files and folders',
    intro: 'The main screen where your images are stored and organized.',
    blocks: [
      {
        title: 'Toolbar',
        items: [
          'Left — workspace name (at root), breadcrumbs (in a subfolder), or active search/filter indicator.',
          'Right — upload, create folder, search, tag filter, sort, grid/list view, trash.',
        ],
        where: 'Bar below the app top header.',
      },
      {
        title: 'View modes',
        items: [
          'Grid — cards with preview, likes, and comment indicator.',
          'List — table: name, date, type, size; handy for bulk selection.',
        ],
        where: 'View toggle button (grid and list icons) on the right of the toolbar.',
      },
      {
        title: 'File search',
        items: [
          'Click the magnifier icon and type at least 2 characters.',
          'Search covers file name, tags, and comment text.',
          'To exit search — click "Clear" or empty the field.',
        ],
        where: 'Magnifier icon in the toolbar. Sort and upload are hidden while search is active.',
      },
      {
        title: 'Tag filter',
        items: [
          'Click the tag icon and select one or more tags.',
          'Selected tags appear as chips on the left — remove any with ×.',
          'With active tags, "Create tag gallery" appears (see Public galleries).',
        ],
        where: 'Tag icon in the toolbar.',
      },
      {
        title: 'Sorting',
        items: [
          'By upload date (newest or oldest first).',
          'By name (A–Z or Z–A).',
          'By file size.',
          'By number of likes.',
        ],
        where: 'Sliders icon in the toolbar. Unavailable in trash, search, or tag filter.',
      },
      {
        title: 'Likes',
        items: [
          'Click the heart on a card to like or unlike.',
          'Liking does not open the file — click the preview or name to open.',
        ],
      },
      {
        title: 'Pagination',
        text: 'When there are many files, use the panel at the bottom to change pages and items per page.',
        where: 'Pagination bar at the bottom of the screen.',
      },
    ],
  },
  selection: {
    title: 'Selection and actions',
    intro: 'How to select multiple files and folders and what you can do with them.',
    blocks: [
      {
        title: 'Selection with mouse',
        items: [
          'Click an item — add or remove from selection.',
          'Shift + click — select a range from the last selected item.',
          'Drag a box on empty space — select everything inside.',
          'Ctrl (Cmd on Mac) + box — add to current selection.',
          'Ctrl+A — select all on the page.',
          'Escape or click outside — clear selection.',
        ],
      },
      {
        title: 'Selection on touch',
        items: [
          'Single tap — open file or folder.',
          'Double tap on a card — add to selection.',
          'Drag on empty space (rubber band) — bulk selection.',
          'Long press on empty space — context menu.',
        ],
      },
      {
        title: 'Drag and drop',
        items: [
          'Drag a file or folder onto another folder to move it.',
          'If the item is already selected, the whole selection moves.',
          'On phone, touch drag shows a floating preview card.',
        ],
        tip: 'Drag and drop is disabled in trash.',
      },
      {
        title: 'Cut and paste',
        items: [
          'Select items → "Cut" in context menu or Ctrl+X.',
          'Go to target folder → "Paste" or Ctrl+V.',
          'Cannot paste into the same folder you cut from.',
        ],
      },
      {
        title: 'Context menu',
        text: 'Right-click the gallery (or long press on touch) for actions.',
        items: [
          'Upload images / Create folder.',
          'Cut, Paste.',
          'Rename folder (exactly one folder selected).',
          'Create gallery (exactly one folder selected).',
          'Add tag (files in selection only).',
          'Generate — selected images (editors and admins).',
          'Delete (moves to trash).',
          'Restore (trash only).',
        ],
        tip: 'Shortcut hints are shown next to menu items.',
      },
      {
        title: 'Bulk operations',
        items: [
          'Delete selection — to trash (with confirmation).',
          'Add tag — to all selected files at once.',
          'Cut and paste — move a group of files.',
        ],
      },
      {
        title: 'AI image generate (gallery)',
        items: [
          'Select one or more images → context menu → Generate.',
          'One new image per selected file, generated one after another. Download or save results — originals are not changed.',
        ],
      },
    ],
  },
  viewer: {
    title: 'File viewer',
    intro: 'Full-screen window with file info, comments, and actions.',
    blocks: [
      {
        title: 'How to open',
        items: [
          'Click a file card in the gallery (or name in list view).',
          'In bulk selection mode — double-click / double tap.',
        ],
      },
      {
        title: 'Navigate between files',
        items: [
          'Previous / Next arrows on the sides of the window.',
          '← and → keys on the keyboard.',
          'Escape — close (or exit fullscreen image view).',
        ],
      },
      {
        title: 'Info tab',
        items: [
          'Name, resolution, size, upload date.',
          'Tags — click a tag to filter the gallery by it.',
          'Buttons: Edit, Download, Create/revoke public link, Copy URL.',
        ],
        where: 'Right panel of the modal → Info tab.',
      },
      {
        title: 'Tags',
        items: [
          'Tag icon in the corner — add a new tag.',
          '× on a tag chip — remove the tag.',
        ],
      },
      {
        title: 'AI image generate',
        items: [
          'Sparkles button next to Tag — describe a background, then generate a new product image from the current file.',
          'Preview stays in the modal: download or save as a new file to a folder (the original is not overwritten).',
        ],
        where: 'Viewer toolbar → sparkles icon (editors and admins).',
      },
      {
        title: 'Public link for a file',
        items: [
          '"Create public link" — anyone with the link can view without signing in.',
          '"Revoke link" — access stops.',
          '"Copy URL" — copy link to clipboard.',
        ],
        tip: 'This is a single-file link. For albums use Public galleries.',
      },
      {
        title: 'Comments tab',
        items: [
          'Write comments and replies on the file.',
          'Edit and delete your own comments.',
          'Add emoji reactions to comments.',
          'Mention colleagues with @ — start typing name or email.',
        ],
        where: 'Right panel → Comments tab. New comment alerts appear in the bell.',
      },
      {
        title: 'Fullscreen view',
        text: 'Click the expand icon on the image to view it fullscreen.',
      },
    ],
  },
  editor: {
    title: 'Image editor',
    intro: 'Open a file from the viewer with "Edit". The canvas is built from layers: frame, images, text, and shapes. All changes can be undone.',
    blocks: [
      {
        title: 'Top bar',
        items: [
          'Zoom in / out / fit to screen — canvas scale.',
          'Undo / Redo — edit history.',
          'Reset — restore original image.',
          'Current zoom (%) and frame pixel dimensions are shown.',
        ],
      },
      {
        title: 'Layers and properties',
        items: [
          'Layers panel (left) — list of frame, images, text, and shapes; select, reorder, lock, delete.',
          'Properties panel (right) — settings for the selected layer.',
          'Click a layer on the canvas or in the list to edit it.',
          'Add image — extra image layers from your workspace.',
          'Ctrl+C — copy the selected layer (image, sticker, text, or shape). The frame cannot be copied.',
          'Ctrl+V — paste the copied layer with a slight offset.',
        ],
        where: 'Toolbar buttons with layers and sliders icons.',
      },
      {
        title: 'Frame (layout)',
        items: [
          'Size, background, corner radius, and border.',
          'Set aspect ratio — pick a preset (1:1, 16:9, 4:3, etc.) for the frame.',
          'When the frame aspect ratio changes, text and shapes stay in place and keep their size; only the visible frame area changes.',
          'The image layer keeps its visual position; you can resize or pan it afterward as usual.',
        ],
        where: 'Properties panel (right) when the frame layer is selected.',
        tip: 'Press Escape to exit aspect ratio mode.',
      },
      {
        title: 'Image layer',
        items: [
          'Select the image layer — the properties panel shows «Image».',
          'Position and size — numeric fields; rotation — slider (−180°…+180°) in the Rotation section.',
          'Right-click the image → resize mode; drag corner handles. Drag inside the image to pan within the frame.',
          'Transform section — Edit with AI (prompt bar at the bottom of the canvas, up to 200 characters), Remove background (uses the shared AI token pool), straighten (angle slider appears at the bottom; click Done), flip horizontal / vertical.',
          'Transform section — Crop (free rectangle + Cut), Lasso (erase inside or keep selection only + checkmark to apply).',
          'Color and Effects sections — brightness, contrast, saturation, and filter presets.',
        ],
        where: 'Properties panel (right) when an image layer is selected.',
        tip: 'Edit with AI sends the layer as you see it (rotation, flip, filters) and replaces it on the canvas. Escape exits resize, straighten, crop, lasso, or the AI prompt. Double tap closes the lasso path on touch devices.',
      },
      {
        title: 'Stickers',
        items: [
          'The "Add sticker" button in the toolbar opens your workspace sticker library.',
          'A new sticker is created when you use Remove background on an image layer — the result is saved to the library and applied to the current layer.',
          'Click a sticker in the panel or drag it onto the canvas to add it as a separate layer.',
          'Stickers behave like images: position, size, rotation, shadow; they appear with a dedicated icon in the layers panel.',
          'The × on a library card permanently removes the sticker from the workspace (layers already on the canvas are not affected).',
        ],
        where: 'Toolbar → sticker icon; stickers panel on the left (next to the layers panel).',
        tip: 'Remove background uses the layer as you see it, including rotation, flip, scale, and filters.',
      },
      {
        title: 'Text layers',
        items: [
          'Add a heading or body text layer from the toolbar.',
          'Font, size, color, stroke, shadow, background, spacing.',
          'Alignment — horizontal (left / center / right) and vertical (top / center / bottom) inside the text box.',
          'Drag text on the canvas; resize the box with corner handles.',
          '"Apply to image" — bake text into the bitmap before saving.',
        ],
        where: 'On mobile, open the properties panel with the sliders icon.',
      },
      {
        title: 'Shapes',
        items: [
          'Rectangle, triangle, circle, line — pick a tool and drag on the canvas to draw.',
          'Fill, stroke, corner radius, shadow, and gradient in properties.',
          'Drag to move; corner handles to resize; top handle to rotate.',
          'Escape cancels the active drawing tool.',
        ],
      },
      {
        title: 'Canvas navigation',
        items: [
          'Mouse: drag to pan; wheel to zoom.',
          'Touch: pinch to zoom; one finger to pan.',
        ],
      },
      {
        title: 'Saving',
        items: [
          'Save — overwrite the original file.',
          'Download — save file to your computer.',
          'Save as — new file (name, PNG/JPG/WebP, size, folder).',
        ],
        where: 'Save button and arrow menu on the right of the editor header.',
      },
    ],
  },
  publicGalleries: {
    title: 'Public galleries',
    intro: 'Beautiful pages to show work to clients — no account required.',
    blocks: [
      {
        title: 'Gallery from a folder',
        items: [
          'Select exactly one folder.',
          'Context menu → "Create gallery" (or Ctrl+Alt+G).',
          'Set name, layout, theme, author info.',
          'Copy the link and send to your client.',
        ],
      },
      {
        title: 'Tag gallery',
        items: [
          'Enable filter by one or more tags.',
          'Click "Create tag gallery".',
          'Content updates automatically when you change file tags.',
        ],
        where: 'Button appears in the toolbar when tag filter is active.',
      },
      {
        title: 'Gallery settings',
        items: [
          'Layout: grid, masonry, or large cards.',
          'Theme: light/dark and accent color.',
          'Generate a business card with QR code.',
        ],
      },
      {
        title: 'Author panel',
        items: [
          'Author mode — name, occupation, contacts, and about text.',
          'Company mode — company name, logo from workspace files, tagline, contacts, and about text.',
          'Switching modes does not clear the other mode\'s data.',
          'Filled fields appear in the public page sidebar and on the digital business card.',
        ],
        where: 'Author section in the create or edit gallery dialog.',
      },
      {
        title: 'Manage galleries',
        items: [
          'Sidebar → "Galleries" — catalog of all public galleries by workspace.',
          'Open, edit, delete, or show the QR business card from the card actions.',
        ],
        where: 'Sidebar → "Galleries" (page /galleries).',
      },
      {
        title: 'What visitors see',
        items: [
          'Image grid with infinite scroll.',
          'Fullscreen view (Escape, ← →).',
          'Author panel with person or company details and contacts.',
          'Download digital business card with QR code to the gallery.',
        ],
      },
    ],
  },
  profile: {
    title: 'Profile and teams',
    intro: 'Personal settings, workspaces, and team members.',
    blocks: [
      {
        title: 'Open profile',
        items: [
          '"Profile" item at the bottom of the sidebar.',
          '"Sign out" — end session (initials menu, top right).',
        ],
        tip: 'Profile opens as a full page; help (question mark) is a separate panel.',
      },
      {
        title: 'Personal tab',
        items: [
          'Change name and email.',
          'Click "Save changes".',
        ],
      },
      {
        title: 'AI tokens tab',
        items: [
          'Shows used and remaining tokens from the shared pool (background removal, image generation, AI edit).',
          'When tokens run out, AI actions are disabled until an admin raises your limit.',
        ],
        where: 'Profile → AI tokens.',
      },
      {
        title: 'Security tab',
        items: [
          'Change password: new password and confirmation.',
          'Password must meet complexity requirements.',
        ],
      },
      {
        title: 'Workspaces tab',
        items: [
          'Create a new workspace (name, language, theme, accent).',
          'Edit or delete your workspace.',
          'Leave someone else\'s workspace.',
          'View storage usage.',
        ],
      },
      {
        title: 'Teams tab',
        items: [
          'Member list for each of your workspaces, with used / max team slots (limit includes the owner).',
          'Change role: Admin, Editor, Viewer (⋮ → Edit role).',
          'Assign visibility: hide folders, tables, or boards from a member (⋮ → Assign visibility).',
          'Remove a member from the workspace.',
        ],
      },
      {
        title: 'Visibility',
        items: [
          'Visibility is a deny-list on top of roles: a hidden resource stays inaccessible even if the role would allow it.',
          'Owner can hide resources from any member. Admin can hide only from Editor and Viewer — not from Owner or another Admin.',
          'Hiding a folder also hides its nested folders and files in the app (lists, search, open by URL).',
          'Tables and boards are hidden separately from folders.',
          'When inviting someone: after choosing a role you can set optional hides before sending.',
          'Public file links still open even if the file sits in a hidden folder.',
          'The workspace root folder cannot be hidden.',
        ],
        where: 'Profile → Teams → ⋮ on a member → Assign visibility. Or Bell → Sent → invite wizard.',
        tip: 'Hides apply only inside that workspace and do not affect other workspaces.',
      },
    ],
  },
  datasets: {
    title: 'Tables',
    intro: 'Spreadsheet-style tables in your workspaces — columns, rows, and inline editing.',
    blocks: [
      {
        title: 'Where to find tables',
        items: [
          'Sidebar → "Data" → "All" — catalog of all tables by workspace (page /data).',
          'Sidebar → "Data": quick list of your tables and shared ones.',
          '"Create" under "Data" — new table in the current workspace.',
          'On the catalog page — open, edit, delete, export, or view schema from the card actions.',
        ],
        where: 'Left sidebar → Data (and "All" for the full catalog).',
      },
      {
        title: 'Opening a table',
        items: [
          'Click a table name in the sidebar or a card on /data — opens the CRM grid at /data/:id.',
          'Scroll loads more rows automatically (100 per batch).',
          'Scroll position is remembered when you return to the same table.',
        ],
      },
      {
        title: 'Columns and schema',
        items: [
          '+ in the header — add a column (name, type, options).',
          'Drag the grip on a header — reorder columns.',
          'Drag the right edge — resize; widths are saved locally.',
          'Click a column name in the header — rename (when schema editing is allowed).',
          '⋮ menu on a header — filter by text, sort A→Z or Z→A, pin the column while scrolling horizontally, reset sort or filter, or delete the column.',
          'Catalog /data → edit on a card — change name, description, and columns.',
        ],
      },
      {
        title: 'Sort and filter',
        text: 'Each table remembers one sort and one text filter for your view. Settings are saved in the browser and apply when rows load — including infinite scroll and table download.',
        items: [
          'Open ⋮ on a column header (always visible on tablet and mobile).',
          'Sort ascending or descending — a small arrow on the column title shows the active direction.',
          'Only one column can be sorted at a time; use "Clear sort" in the same menu.',
          'Filter — type text and click Apply (or press Enter) to keep rows where the column contains that text.',
          'When a filter is active, a small filter icon appears next to the column title (like the sort arrow).',
          'Use "Clear filter" to reset; changing sort or filter reloads rows from the server.',
        ],
        where: 'Column header → ⋮ menu.',
        tip: 'Sort and filter are stored per table on this device and are used when you export from the tables catalog.',
      },
      {
        title: 'Reference lists',
        text: 'A reference list is a small “dictionary table” in your workspace — statuses, categories, SKUs, and other values you want in one place instead of copying them into every table.',
        items: [
          'Create one: sidebar → Data → Create → turn on "Create reference list". Fill it manually or import from CSV/Excel right away.',
          'Inside there is a single column of unique values. It works like a normal grid: add, edit, and delete rows directly.',
          'Catalog /data → pencil on a list: name, description, rename the key column, and the "List values" section.',
          'Use the download button there to export — it respects any filter or sort you set in the grid.',
          'To import into an existing list, open it and choose Import CSV/Excel — pick the file column with keys; duplicates in the file are skipped.',
          'Lists are marked with a compact "List" badge in the sidebar and on the catalog page.',
        ],
        where: 'Create: sidebar → Data → Create. Manage: sidebar → Data → All (/data).',
        tip: 'One list per workspace = one source of truth for statuses, seasons, SKUs, and similar fields.',
      },
      {
        title: 'Reference column in a table',
        text: 'When a table cell should pick from a shared list — not free text — add a column of type Reference and link it to the list you need.',
        items: [
          'Select — options live only in that column. Good for a few local choices.',
          'Reference — values come from a workspace reference list. Good when the same set is used in several tables.',
          'Add the column via + in the grid header, or catalog /data → edit table → Add column → type Reference → pick the list.',
          'In the cell you get a searchable dropdown. After you save the row, the value stays tied to the list entry.',
          'If you rename a value in the list, the table shows the updated label.',
        ],
        where: 'Grid header → +, or Data → All → edit on a card.',
        tip: 'Create and fill the list first, then add reference columns in your working tables.',
      },
      {
        title: 'Lists and files',
        text: 'You can link table rows to gallery files and automatically update file tags when a Reference column value changes.',
        items: [
          'Step 1 — File linking: open the table → "File linking" → choose the column used to match files (often SKU or code) and how it maps to tags.',
          'Once set up, the grid shows a column with linked file previews; from the image viewer you can see related rows.',
          'Step 2 — File action on a Reference column: when adding or editing the column, set "File action" to Add tag or Replace tag in list.',
          'It runs when the row already has linked files and you change that cell — tags on those files update automatically (tag name = the list value).',
          'Without File linking configured first, tag actions will not run — set up the table-to-gallery link before enabling them.',
        ],
        where: 'Table → File linking; tag action — in Reference column settings.',
        tip: 'Typical flow: SKU links a row to product photos; changing the status in the list auto-tags files as "On sale" or "Archive".',
      },
      {
        title: 'Merging tables',
        text: 'Combine two existing tables into a read-only merged view. Rows are matched by linking columns — useful for lookups across datasets.',
        items: [
          'Sidebar → "Data" → "Merge" — opens a three-step wizard.',
          'Step 1: name and workspace for the merged table.',
          'Step 2: pick the main table and second table, then choose join columns (the second table\'s link column must be unique).',
          'Step 3: select which columns from each table appear in the result.',
          'Merged tables open like regular grids, but rows and schema cannot be edited — only column order, filters, and sort.',
          'In the sidebar and on the /data catalog, merged tables show a "Merge" badge.',
        ],
        where: 'Left sidebar → Data → Merge (next to Create).',
        tip: 'Prepare both source tables first, with a shared key column (e.g. SKU or ID) marked unique in the second table.',
      },
      {
        title: 'Rows and cells',
        items: [
          'Click a cell to edit; Enter saves, Escape cancels.',
          'Tab / Shift+Tab — move to the next or previous cell.',
          '"Add row" at the bottom — new row (draft cancels on click outside if empty).',
          'Trash icon on a row — delete one row.',
          'Checkboxes + ⋮ in the header — bulk delete selected rows.',
        ],
        tip: 'Changes appear immediately; the app syncs with the server in the background.',
      },
      {
        title: 'Permissions',
        items: [
          'Owner / Admin — edit schema, rows, and metadata.',
          'Editor — edit rows only.',
          'Viewer — read-only grid.',
        ],
      },
    ],
  },
  boards: {
    title: 'Boards',
    intro: 'Kanban boards over your tables — columns from a reference (status) list, cards from table rows.',
    blocks: [
      {
        title: 'Where to find boards',
        items: [
          'Sidebar → "Boards" → "All" — catalog of boards by workspace (page /boards).',
          'Sidebar → "Boards": quick list of your boards and shared ones.',
          '"Create" under "Boards" — new board in the current workspace.',
          'On the catalog — open, edit board settings, or delete from the card menu.',
        ],
        where: 'Left sidebar → Boards (and "All" for the full catalog).',
      },
      {
        title: 'Creating a board',
        items: [
          'Step 1: name, optional description, and workspace.',
          'Step 2: pick an eligible table and a reference column for statuses; choose which status values become columns (at least one).',
          'Step 3: choose the title field and optional extra fields shown on cards.',
          'After create you land on the board page at /boards/:id.',
        ],
        tip: 'Only ordinary tables with a reference column can be used. Merge and list datasets are not offered.',
      },
      {
        title: 'Kanban board',
        items: [
          'Columns follow the board\'s status order; drag a column handle to reorder (admin).',
          'Cards show the title and up to a few preview fields.',
          'A message icon with a count appears on a card when it has comments.',
          'A paperclip icon with a count appears when the card has attached files.',
          'Drag cards between columns or within a column to change status and order.',
          'Live updates via SSE — other users see moves, edits, comments, and file attach/detach without refresh.',
        ],
      },
      {
        title: 'Cards',
        items: [
          'Click a card — opens a view modal with card fields, a Comments tab, and a Files tab.',
          '⋮ → Edit — side panel to edit the table row.',
          '⋮ → Delete — removes the row from the table after confirm.',
          '"Add card" under a column — creates a row with that status.',
        ],
        where: 'Board page → card or column footer.',
        tip: 'Deep link /boards/:id?card=:rowId opens the same card modal (e.g. from the bell).',
      },
      {
        title: 'Card comments',
        items: [
          'Comments belong to this board + card (row), not shared across boards on the same table row.',
          'Anyone with workspace access (viewer+) can comment; author or admin can delete others\' comments.',
          'New comments notify workspace members in the bell.',
        ],
      },
      {
        title: 'Card files',
        items: [
          'Files tab — attach images from the board workspace (same picker as in the image editor).',
          'Attachments belong to this board + card (row), not shared across boards on the same table row.',
          'Click a preview to open the image viewer; hover shows the file name; ✕ detaches without deleting the file from the gallery.',
          'If a file is in Trash, the attachment stays with an “in trash” overlay; permanently deleting the file removes the attachment.',
          'Anyone with workspace access (viewer+) can attach and detach.',
        ],
        where: 'Card modal → Files tab.',
      },
      {
        title: 'Columns',
        items: [
          'Admin: add a column (new or existing status value), rename, reorder, or remove.',
          'A column with cards cannot be removed — move or delete cards first.',
          'Removing a column hides it on this board only; the list value stays in the reference list.',
        ],
      },
      {
        title: 'Assignees and deadlines',
        items: [
          'Open a card → Assignees: add workspace members, each with their own deadline and target column.',
          'Only Owner / Admin can assign, change deadlines/targets, or set priority (from lowest to highest).',
          'Editor sees all assignees; Viewer sees only their own assignment on the card.',
          'When a card is dragged into an assignee\'s target column, that person\'s task completes; dragging back reopens it.',
          'Several people with the same target column all complete on one move.',
          'Overdue assignees show a red hint on the card; alerts go to the bell on login/token refresh (no background cron).',
          'You get personal alerts for your own deadlines (due soon / today / overdue). Owner/Admin also get a digest when someone else is overdue.',
        ],
        where: 'Board → card modal → Assignees (and Priority under fields).',
        tip: 'Changing a deadline resets alert state so the next login/refresh can notify again.',
      },
      {
        title: 'Permissions',
        items: [
          'Viewer — view board, drag cards, write card comments, attach/detach files; see only own assignees.',
          'Editor — create, edit, and delete cards (rows); see all assignees but cannot assign.',
          'Owner / Admin — board settings, columns, delete board, moderate comments, assign people, set deadlines/targets and priority.',
        ],
      },
    ],
  },
  workflows: {
    title: 'Workflows',
    intro: 'Workflows are a product layer next to Files, Data, Boards, and Galleries — not a settings screen. You build an automation graph that moves files, rows, and cards through systems you already set up.',
    blocks: [
      {
        title: 'Not settings',
        items: [
          'Workflows live in their own sidebar section — same idea as Data or Boards, not under Profile / settings.',
          'Each process belongs to a workspace and uses that space’s folders, tables, boards, patterns, and tags.',
          'Enable turns the process on; Save stores the graph. Runs show what actually happened.',
        ],
        tip: 'If you are looking for language, theme, or account options — that is Profile, not Workflows.',
      },
      {
        title: 'Where to find Workflows',
        items: [
          'Sidebar → "Workflows" → "All" — catalog by workspace (/workflows).',
          'Sidebar → "Workflows" → "Current" — quick list for the active workspace (hidden if empty).',
          '"Create" under Workflows — new graph (owner/admin only).',
          'Open a process — graph builder; Runs — journal of executions.',
        ],
        where: 'Left sidebar → Workflows.',
      },
      {
        title: 'Graph builder',
        items: [
          'Palette — groups: Triggers, Files, Content, AI, Table, Board, Flow; plus your Templates panel.',
          'Canvas — place nodes, connect edges, drag to rearrange; Undo / Redo after the title (also Ctrl+Z / Ctrl+Y).',
          'Inspector — configure the selected trigger or node (folder, tags, table, cron schedule, and so on).',
          'Exactly one trigger. Most steps form a chain; If / branch can split into True and False outs.',
          '"Validate" checks required fields and whether the stream (files / rows) makes sense before Save.',
          'Keyboard: copy / paste nodes (Ctrl+C / Ctrl+V), delete selection (Delete / Backspace) — see Shortcuts.',
        ],
        tip: 'Cron can run once, daily, weekly (weekday + time), or monthly (day of month + time), in your timezone.',
      },
      {
        title: 'Palette: Triggers',
        text: 'Exactly one trigger per process.',
        items: [
          'File uploaded — starts when a file is uploaded to the watched folder.',
          'Row added — starts when a new row appears in the table.',
          'Rows imported — starts after a CSV/Excel import into the table.',
          'Cell matched — starts when a cell in the chosen column changes to a matching value.',
          'File moved — starts when a file is moved into the watched folder.',
          'On schedule — starts on a cron schedule (the run begins empty).',
        ],
      },
      {
        title: 'Palette: Files',
        items: [
          'Apply tags — adds tags to files in the flow.',
          'Remove tags — removes tags from files in the flow.',
          'Move file — moves files to the target folder.',
          'Trash file — sends files to the trash.',
          'Linked rows — finds table rows linked to files in the flow.',
        ],
      },
      {
        title: 'Palette: Content',
        items: [
          'Apply pattern — applies a workspace pattern to each file scene.',
          'Save as — saves a copy of each file into the target folder.',
          'Replace content — replaces each file with the cutout from Remove background.',
        ],
      },
      {
        title: 'Palette: AI',
        items: [
          'Remove background — cuts out the background on each image (uses AI tokens).',
          'Generate image — creates one new image per file in the flow.',
        ],
        tip: 'AI nodes use the shared AI token pool from your profile.',
      },
      {
        title: 'Palette: Table',
        items: [
          'Set cells — writes values into chosen columns of rows in the flow.',
          'Insert row — adds a new row to the table.',
          'Linked files — loads files linked to rows into the file stream.',
        ],
      },
      {
        title: 'Palette: Board',
        items: [
          'Add priority — sets priority on cards in the flow.',
          'Clear priority — removes priority from cards in the flow.',
          'Add assignment — assigns a member (with deadline/column) to cards.',
          'Unassign — removes assignees from cards in the flow.',
        ],
      },
      {
        title: 'Palette: Flow',
        items: [
          'Table source — feeds table rows into Generate image (data handle).',
          'Search — loads workspace files into the flow by tags, name, or extension.',
          'Filter — keeps only flow items that match the criteria.',
          'Link — matches files to dictionary values by name (optional tag).',
          'Limit — keeps only the first N items in the flow.',
          'If / branch — routes the run to True or False without changing the stream.',
        ],
      },
      {
        title: 'Who can do what (read-only for editors)',
        items: [
          'Owner / Admin — create, edit, save, enable/disable, delete, and manage templates.',
          'Editor / Viewer — open the catalog and the graph in read-only mode (no palette, no Save), and open Runs.',
        ],
        tip: 'Editors can study how a process is built and how it ran — they do not change the automation.',
      },
      {
        title: 'Runs / journal',
        items: [
          'Every run records the trigger and each node step: ok, skipped, or failed, with timing and details.',
          'Open a run to see the graph with step badges and the step list.',
          'Runs are available to every workspace member.',
        ],
        where: 'Process → Runs, or a catalog card → Runs.',
      },
      {
        title: 'User templates',
        items: [
          'Owner / Admin: Save menu → "Save as template" — stores the current graph in the workspace library.',
          'Templates panel (left) — apply a template onto the canvas (a fresh copy with new ids), or delete a template.',
          'Applying a template does not keep a live link — you edit the copy like any other process.',
        ],
        tip: 'Templates are per workspace. Editors cannot create or apply them.',
      },
    ],
  },
  notifications: {
    title: 'Notifications and invitations',
    intro: 'The bell in the top bar is your inbox for team and comment events.',
    blocks: [
      {
        title: 'Comments tab',
        items: [
          'Alerts for new comments and replies on your files.',
          'Alerts for new comments on board cards.',
          'Deadline alerts: due soon (24h), due today, overdue — for cards where you are an assignee.',
          'Owner/Admin digest: someone else on your boards is overdue (about once per day per assignment).',
          'Click a file notification — opens the image and the Comments tab.',
          'Click a board-card or deadline notification — opens /boards/:id?card=:rowId with the card modal.',
          '"Clear all" — remove all notifications for the current workspace.',
        ],
        where: 'Bell → message icon tab (opens by default).',
      },
      {
        title: 'Incoming tab',
        items: [
          'Invitations to join someone\'s workspace.',
          'Accept or decline.',
          'Shows: who invited, workspace name, role, expiry.',
        ],
      },
      {
        title: 'Sent tab',
        items: [
          'Invitations you sent.',
          'Cancel a pending invitation.',
          '"Add member" — invite a new user.',
        ],
      },
      {
        title: 'How to invite a user',
        items: [
          'Bell → Sent → "Add member".',
          'Step 1: find user by email.',
          'Step 2: pick workspace and role (Admin / Editor / Viewer). Pending invites count toward the team limit.',
          'When the workspace team is full, invite is disabled until a system admin raises your member limit.',
          'Step 3: optionally hide folders, tables, or boards, then send the invitation.',
        ],
      },
      {
        title: 'Badge on the bell',
        text: 'The red badge shows the total of pending invitations and comment alerts for the current workspace.',
      },
    ],
  },
  trash: {
    title: 'Trash',
    intro: 'Deleted files and folders go here instead of disappearing immediately.',
    blocks: [
      {
        title: 'How to open',
        items: [
          'Click "Trash" in the toolbar on the home screen.',
          'Back button in trash header — return to gallery.',
        ],
        where: 'Trash icon on the right of the toolbar.',
      },
      {
        title: 'Restore',
        items: [
          'Select items → context menu → "Restore".',
          'Shortcut: Ctrl+Alt+R.',
          'Items return to their original location.',
        ],
      },
      {
        title: 'Delete permanently',
        items: [
          '× on a card — delete one file or folder forever (owner only).',
          '"Empty trash" — delete everything (with confirmation, owner only).',
        ],
        tip: 'After emptying trash, files cannot be recovered.',
      },
      {
        title: 'Trash limitations',
        items: [
          'Cannot upload files or create folders.',
          'Cannot drag and drop items.',
          'Sorting is unavailable.',
        ],
      },
    ],
  },
  shortcuts: {
    title: 'Shortcuts and gestures',
    intro: 'Quick reference. Press Shift+? to open this cheat sheet anytime. Shortcuts are disabled when the image editor is open, focus is in an input field, or a process is read-only.',
    blocks: [
      {
        title: 'Gallery',
        items: [
          'Ctrl+A — select all.',
          'Ctrl+U — upload files.',
          'Ctrl+Shift+V — paste screenshot from clipboard.',
          'Ctrl+Alt+N — create folder.',
          'Ctrl+X — cut.',
          'Ctrl+V — paste.',
          'F2 — rename folder (one selected).',
          'Ctrl+Alt+G — create gallery (one folder).',
          'Ctrl+Alt+T — add tag.',
          'Delete — move to trash.',
          'Ctrl+Alt+R — restore (in trash).',
          'Escape — clear selection.',
        ],
      },
      {
        title: 'File viewer',
        items: [
          '← / → — previous / next file.',
          'Escape — close window.',
        ],
      },
      {
        title: 'Editor',
        items: [
          'Ctrl+Z — undo.',
          'Ctrl+Y — redo.',
          'Ctrl+0 — fit to screen.',
          'Ctrl++ — zoom in.',
          'Ctrl+- — zoom out.',
          'H — flip horizontal (selected image).',
          'V — flip vertical (selected image).',
          'T — straighten mode.',
          'C — free crop.',
          'L — lasso erase inside.',
          'Shift+L — lasso keep selection only.',
          'Ctrl+C — copy selected layer (not the frame).',
          'Ctrl+V — paste layer.',
          'Delete / Backspace — delete selected layer (text, image, or shape).',
          'Escape — exit current mode (aspect ratio, resize, shape tool, crop, lasso) or reset changes.',
        ],
        tip: 'Shortcut hints appear in editor button tooltips (on desktop with mouse). Frame aspect ratio and layer tools have no letter shortcuts — use the properties panel.',
      },
      {
        title: 'Tables (CRM grid)',
        items: [
          'Enter — save cell or start editing the focused cell.',
          'Escape — cancel cell edit.',
          'Tab / Shift+Tab — next / previous editable cell.',
        ],
        where: 'Open any table from the sidebar.',
      },
      {
        title: 'Process editor (workflows)',
        items: [
          'Ctrl+Z — undo.',
          'Ctrl+Y or Ctrl+Shift+Z — redo.',
          'Ctrl+C — copy selected nodes.',
          'Ctrl+V — paste nodes.',
          'Delete / Backspace — delete selected nodes.',
        ],
        where: 'Open a process from the sidebar (Workflows).',
        tip: 'Shortcuts use physical keys (work on non-Latin layouts). Disabled while focus is in an input field or the process is read-only.',
      },
      {
        title: 'Touch gestures',
        items: [
          'Single tap — open; double tap — select.',
          'Rubber band — bulk selection.',
          'Long press — context menu.',
          'Pinch in editor — zoom.',
        ],
      },
    ],
  },
};
